import { Link } from 'react-router-dom';
import DeckMenu from './DeckMenu.tsx';
import { DECK_DRAG_MIME } from '../../components/CategorySidebar.tsx';
import { relativeTime, colourHex, formatBuiltBy, formatBuiltByShort } from '../../lib/format.ts';
import { Refresh } from '../../lib/icons.tsx';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { CoverWarm } from '../../lib/coverWarm.ts';
import type { DeckSummary } from '../../../shared/deck.ts';
import type { CategoryColour } from '../../../shared/api.ts';
import { swatchVar } from '../../../shared/palette.ts';
import type { DeckCardActions } from './DeckMenu.tsx';

export type { DeckCardActions } from './DeckMenu.tsx';

/** The list shell. A component rather than a bare `<ul>` in the route so the
 *  surface (panel, hairline dividers, rounded ends) is described in one place
 *  alongside the row that sits on it. */
export function DeckRows({ children }: { children: ReactNode }) {
  return (
    <ul className="deck-rows" role="list">
      {children}
    </ul>
  );
}

/** Deliberately a near-copy of `DeckCardProps`, so the route's map body is the
 *  same in both views and switching one for the other is a component swap.
 *  What is missing is what a row has no room for: `tileSize` (rows have one
 *  size) and `overviewLoading` (the zoom button does not appear — see below). */
type DeckRowProps = {
  deck: DeckSummary;
  /** Identity of this row's menu within the page's single open-menu slot. */
  cardKey: string;
  /** The cached cover URL, or null while there isn't one. */
  cover: string | null;
  /** The dashboard's warm queue — placeholder state, retry, and the observer
   *  ref that turns "this row is on screen" into "render its cover". The SAME
   *  queue the grid uses; a second one would race it for the Vite pool. */
  warm: CoverWarm;
  busy: boolean;
  isSelected: boolean;
  /** Something, anywhere in the list, is ticked: the checkboxes stay visible. */
  isSelecting: boolean;
  isDragging: boolean;
  menuOpen: boolean;
  /** A category's registry tint, for its chip dot. Optional: a caller with no
   *  registry to hand renders the chips untinted rather than nothing. */
  categoryColour?: (path: string) => CategoryColour | null;
  onToggleSelected: (slug: string, shiftKey: boolean) => void;
  setMenuKey: Dispatch<SetStateAction<string | null>>;
  setDraggingSlug: Dispatch<SetStateAction<string | null>>;
  actions: DeckCardActions;
};

/**
 * One deck as a row: thumb, name, chips, counts, kebab.
 *
 * The row is a FIXED 86px whatever the deck carries, which is the whole point
 * of the view — a hundred decks scan as a hundred evenly spaced lines. Every
 * cell therefore truncates rather than wraps, and nothing here may grow the
 * row: a two-line title in one row and one in the next is the grid's problem,
 * not this view's.
 *
 * Two things the grid card has are deliberately absent. The **zoom button**
 * ("see all slides") wants a cover big enough to sit on and a row that isn't
 * already six columns wide; the deck's own kebab reaches everything else, and
 * the grid keeps the shortcut. The design's **owner avatar and star** are not
 * built at all: Studio has no identity and no starred flag, so both would be
 * dead controls (see `plans/studio-homepage-redesign.md`).
 */
export default function DeckRow({
  deck: d,
  cardKey,
  cover,
  warm,
  busy,
  isSelected,
  isSelecting,
  isDragging,
  menuOpen,
  categoryColour,
  onToggleSelected,
  setMenuKey,
  setDraggingSlug,
  actions,
}: DeckRowProps) {
  const { warmingNow, coverStalled, retryCover, coverRef } = warm;
  // A broken or empty deck has no slide 1 to show, so it is never given a slot
  // nor queued for one. Everything else gets the same cached 640×360 WebP the
  // grid and the deck overview use.
  const showCover = !d.broken && d.slideCount > 0;
  const modelLabel = d.builtBy ? formatBuiltByShort(d.builtBy) : '';
  return (
    <li
      className={
        'deck-row' +
        (menuOpen ? ' deck-row--menu-open' : '') +
        (isSelected ? ' is-selected' : '') +
        (isSelecting ? ' is-selecting' : '') +
        (isDragging ? ' is-dragging' : '')
      }
      // Dragging a row onto a sidebar category files it there — the same drag
      // source, payload and MIME as the card, so the rail needs no idea which
      // view it was dropped from. The ROW is the source, not the link inside
      // it (see draggable={false} on __main): otherwise the browser drags the
      // URL with a payload the rail can't read.
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(DECK_DRAG_MIME, d.slug);
        e.dataTransfer.setData('text/plain', d.title); // dropping elsewhere pastes something sane
        e.dataTransfer.effectAllowed = 'copy';
        setDraggingSlug(d.slug);
        setMenuKey(null);
      }}
      onDragEnd={() => setDraggingSlug(null)}
    >
      {/* Select for a bulk verb, in its own 22px slot BESIDE the thumb rather
          than over it — laid on the cover it hid the picture of the row you
          were pointing at. Hidden until hover (or until something IS selected)
          so the resting list stays a list of decks, not a form, but hidden by
          opacity: it is a real checkbox for keyboard and screen readers at all
          times, and its space is reserved so revealing it shifts nothing.
          Shift-click extends from the last one ticked. */}
      <label className="deck-row__select" onMouseDown={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            // React's onChange gives no shiftKey, so the range lives here and
            // onChange is left off entirely (the box is driven by `checked`).
            onToggleSelected(d.slug, e.shiftKey);
          }}
          onChange={() => {}}
          aria-label={`Select ${d.title}`}
        />
      </label>
      <Link to={`/editor/${d.slug}`} className="deck-row__main" draggable={false}>
        {/* aria-hidden: the title sits right beside it and names the deck — but
            NOT when the box holds the retry control, because a focusable
            element inside an aria-hidden subtree is reachable by keyboard and
            invisible to a screen reader at the same time. The button names
            itself.
            ref: registers the row with the warm queue's observer — scrolling to
            a row is what asks for its cover. */}
        <span
          className="deck-row__thumb"
          aria-hidden={showCover && coverStalled(d.slug) && !cover ? undefined : 'true'}
          ref={showCover && !cover ? coverRef(d.slug) : undefined}
        >
          {/* The section signature, as a 4px bar across the top of the thumb —
              the card's stripe at row scale. */}
          <span className="deck-row__stripe">
            {(d.sections || [])
              .filter((s) => s.n !== null)
              .map((s) => (
                <span key={s.key} className="deck-row__band" style={{ background: colourHex(s.color) }} />
              ))}
          </span>
          {showCover &&
            (cover ? (
              // draggable={false} is load-bearing: the ROW is the drag source
              // for filing into a category, and an <img> is natively draggable,
              // so without this it hijacks the drag with an image payload the
              // rail can't read.
              <img className="deck-row__cover" src={cover} alt="" loading="lazy" decoding="async" draggable={false} />
            ) : coverStalled(d.slug) ? (
              // Given up on for this session. A permanently grey box with
              // nothing to press breaks the studio's rule that a warning always
              // offers a way out, so this is the card's retry control at row
              // size.
              <button
                type="button"
                className="deck-row__retry"
                title="This cover didn't render. Try again."
                aria-label={`Retry the cover for ${d.title || d.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  retryCover(d.slug);
                }}
              >
                <Refresh size={14} />
              </button>
            ) : (
              // Animated only while something is actually coming: a ghost for a
              // deck nothing is working on promises an image that never lands.
              <span className={'deck-row__ph' + (warmingNow(d.slug) ? ' is-working' : '')} />
            ))}
        </span>
        <span className="deck-row__text">
          {/* title attribute on both lines: truncation is the rule here, so the
              full name and the full slug have to be recoverable without opening
              the deck. */}
          <span className="deck-row__title" title={d.title}>
            {d.title}
            {d.broken && <span className="badge badge--warn">Broken</span>}
          </span>
          <span className="deck-row__slug" title={d.slug}>
            {d.slug}
          </span>
        </span>
        {/* Status, filing, provenance — the card's chip family, one row wide.
            Drafts show no pill, exactly as on the card: the absence IS the
            answer, and a grey "Draft" on every unpublished deck is noise. */}
        <span className="deck-row__tags">
          {d.publishedUrl && (
            <span className="row-chip row-chip--ok">
              <span className="row-chip__dot" aria-hidden="true" />
              Published
            </span>
          )}
          {d.category && (
            <span className="row-chip" title={`Category: ${d.category}`}>
              {/* The dot's colour is a SWATCH NAME from the registry, never a
                  literal — the same set the category picker offers. */}
              <span
                className="row-chip__dot"
                style={{
                  background: categoryColour?.(d.category) ? swatchVar(categoryColour(d.category)) : 'var(--faint)',
                }}
                aria-hidden="true"
              />
              {d.category.replace('/', ' › ')}
            </span>
          )}
          {modelLabel && (
            <span className="row-chip row-chip--engine" title={`Built by ${formatBuiltBy(d.builtBy)}`}>
              {modelLabel}
            </span>
          )}
        </span>
        <span className="deck-row__slides">{d.slideCount || '–'} slides</span>
        <span className="deck-row__updated">{relativeTime(d.lastEditedAt)}</span>
      </Link>
      <DeckMenu
        deck={d}
        cardKey={cardKey}
        menuOpen={menuOpen}
        busy={busy}
        setMenuKey={setMenuKey}
        actions={actions}
        className="deck-row__menu iconbtn"
      />
    </li>
  );
}
